import React, { useState, useEffect } from 'react';
import { registry } from '../../core/Registry.js';
import { BaseWidget } from '../BaseWidget.jsx';

export function AnimatedHeadlineRender({ node, isLiveMode }) {
  const content = node.content || {};
  const prefix = content.prefix || 'Wir lieben ';
  const words = (content.words || 'Innovation, Technologie, Kunden, 5G').split(',').map(w => w.trim()).filter(Boolean);
  const suffix = content.suffix || '!';
  
  const animationType = content.animationType || 'typewriter'; // typewriter, fade
  const highlightColor = content.highlightColor || 'var(--tef-primary, #00A9E0)';
  const align = content.align || 'center';

  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    // Im Editor (LiveMode false) überspringen wir komplexe Timeouts um Ruckler beim Drag&Drop zu vermeiden.
    // Wir zeigen dort einfach das erste Keyword an.
    if (!isLiveMode || animationType !== 'typewriter') {
      setCurrentText(words[0] || '');
      return;
    }

    let timeoutId;
    const currentWord = words[currentWordIndex];

    const type = () => {
      if (isDeleting) {
        setCurrentText(prev => prev.slice(0, -1));
        if (currentText.length === 0) {
          setIsDeleting(false);
          setCurrentWordIndex((prev) => (prev + 1) % words.length);
        }
      } else {
        setCurrentText(prev => currentWord.slice(0, prev.length + 1));
        if (currentText.length === currentWord.length) {
          timeoutId = setTimeout(() => setIsDeleting(true), 1500);
          return;
        }
      }
      timeoutId = setTimeout(type, isDeleting ? 50 : 150);
    };

    timeoutId = setTimeout(type, 50);
    return () => clearTimeout(timeoutId);
  }, [currentText, isDeleting, currentWordIndex, words, isLiveMode, animationType]);

  // CSS Animation for Fade
  useEffect(() => {
    if (!isLiveMode || animationType !== 'fade') return;
    const interval = setInterval(() => {
      setCurrentWordIndex(prev => (prev + 1) % words.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isLiveMode, animationType, words.length]);

  return (
    <h2 style={{ 
      textAlign: align,
      margin: 0,
      fontSize: '2rem',
      fontWeight: 'bold',
      color: '#f8fafc',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
      gap: '8px'
    }}>
      <span>{prefix}</span>
      <span style={{ 
        color: highlightColor,
        borderBottom: animationType === 'typewriter' && isLiveMode ? `2px solid ${highlightColor}` : 'none',
        paddingRight: animationType === 'typewriter' ? '4px' : '0',
        transition: animationType === 'fade' ? 'opacity 0.5s ease' : 'none', // Simple Fade 
        opacity: 1
      }}>
        {animationType === 'typewriter' ? currentText : words[currentWordIndex]}
      </span>
      <span>{suffix}</span>
    </h2>
  );
}

registry.register('AnimatedHeadline', {
  label: 'Animierte Überschrift',
  icon: '✨', 
  category: 'advanced', // oder premium
  component: AnimatedHeadlineRender,
  
  defaultProps: {
    content: {
      prefix: 'O2 Mobile 5G ist ',
      words: 'schnell, zuverlässig, überall',
      suffix: '',
      animationType: 'typewriter',
      highlightColor: '#00A9E0',
      align: 'center'
    },
    style: {
      desktop: {
        marginBottom: '20px'
      }
    }
  },

  inspectorControls: [
    {
      tab: 'content',
      groups: [
        {
          title: 'Texte',
          fields: [
            { name: 'content.prefix', label: 'Davor (statischer Text)', type: 'text' },
            { name: 'content.words', label: 'Wechselnde Wörter (kommasepariert)', type: 'textarea' },
            { name: 'content.suffix', label: 'Danach (statischer Text)', type: 'text' }
          ]
        },
        {
          title: 'Animation & Styling',
          fields: [
            { name: 'content.animationType', label: 'Animation', type: 'select', options: ['typewriter', 'fade'] },
            { name: 'content.highlightColor', label: 'Farbe für animierten Text', type: 'color' },
            { name: 'content.align', label: 'Ausrichtung', type: 'select', options: ['left', 'center', 'right'] }
          ]
        }
      ]
    }
  ]
});
